import PropTypes from 'prop-types';
import React from 'react';
import { UX2 } from '@wsb/guac-widget-core';
import FormattersStore from '../stores/Formatters';
import dataAids from '../constants/dataAids';
import DurationAndCost from './DurationAndCost';
import { Truncate } from '@wsb/ola-widget-components';
import { trackMetric } from '../../common/utils/trackMetric';
import { SERVICE_MODIFY_CTA_CLICK } from '../constants/metrics';
import ConfigStore from '../../common/stores/Config';
import { useStore } from '../hooks/useStore';

const descriptionStyles = {
  serviceInfoWrapper: {
    '@xs-only': {
      paddingVertical: 'xsmall'
    },
    '@sm': {
      paddingBottom: 'medium'
    }
  },
  descriptionWrapper: {
    display: 'flex',
    flexWrap: 'nowrap',
    '@xs-only': {
      marginTop: 'xsmall'
    },
    '@sm': {
      marginTop: 'medium'
    }
  },
  descriptionMobile: {
    '@xs-only': {
      display: 'block',
      maxWidth: '100%'
    },
    '@sm': {
      display: 'none'
    }
  },
  descriptionDesktop: {
    '@xs-only': {
      display: 'none'
    },
    '@sm': {
      display: 'block',
      maxWidth: '100%'
    }
  },
  descriptionImage: {
    backgroundSize: 'contain',
    marginTop: 'small',
    maxHeight: '320px',
    textAlign: 'left',
    '@xs-only': {
      display: 'block'
    }
  },
  descriptionMobileBackgroundColor: {
    '@xs-only': {
      backgroundColor: '!rgba(0,0,0,.05)'
    }
  },
  truncate: {
    container: {
      flexDirection: 'column',
      display: 'flex'
    },
    truncated: {
      whiteSpace: 'pre-line'
    },
    expanded: {
      whiteSpace: 'pre-line'
    }
  }
};

export function ServiceInfo(props) {
  const onClickModify = () => {
    props.modifyLink();
    trackMetric(SERVICE_MODIFY_CTA_CLICK);
  };

  const { dateTimeFormatter } = FormattersStore.getState();
  const {
    service,
    serviceStyles,
    selectedAvailableTime,
    modifyLink,
    isDescriptionShown,
    isDescriptionTrimmable,
    staticContent,
    isImageShown,
    depositShown
  } = props;
  const { Block, Heading, Text, Link, Image, MoreLink } = UX2.Element;
  const styles = {
    ...serviceStyles,
    ...descriptionStyles
  };
  const { service_view_max_description_ellipse_characters: maxDescriptionEllipseCharacters } =
    useStore(ConfigStore);

  return (
    <Block style={ styles.serviceInfoWrapper }>
      <Heading.Product style={ styles.name } data-aid={ dataAids.SERVICE_INFO_SERVICE_NAME }>
        { service.name }
      </Heading.Product>
      { selectedAvailableTime && (
        <Block style={ styles.date }>
          <Text
            data-aid={ dataAids.SERVICE_INFO_AVAILABILITY }
            tag='span'
            style={{
              marginBottom: 'none',
              marginRight: modifyLink ? 'xsmall' : 'none'
            }}
          >
            { dateTimeFormatter.formatDateTime(selectedAvailableTime) }
          </Text>
          { modifyLink && (
            <Link data-aid={ dataAids.SERVICE_INFO_MODIFY_LINK } onClick={ onClickModify } tag='span'>
              { staticContent.modify }
            </Link>
          ) }
        </Block>
      ) }
      <DurationAndCost
        service={ service }
        staticContent={ staticContent }
        depositShown={ depositShown }
        featured
      />
      { service.image_url && isImageShown && (
        <Block style={ styles.descriptionMobileBackgroundColor }>
          <Image
            data-aid={ dataAids.SERVICE_INFO_IMAGE_URL }
            src={ `${service.image_url}/:/rs=h:{640}` }
            style={ styles.descriptionImage }
          />
        </Block>
      ) }
      { isDescriptionShown && !!service.description && (
        <Block style={ styles.descriptionWrapper } data-aid={ dataAids.SERVICE_INFO_DESCRIPTION }>
          <Text style={ styles.descriptionDesktop }>{ service.description }</Text>
          <Text style={ styles.descriptionMobile }>
            { isDescriptionTrimmable &&
            service.description.length > maxDescriptionEllipseCharacters + 25 ? (
                <Truncate
                  expandCtaAs={ MoreLink.Expand }
                  i18n={{ expandCta: staticContent.more }}
                  maxEllipseCharacters={ maxDescriptionEllipseCharacters }
                  style={ styles.truncate }
                >
                  { service.description }
                </Truncate>
              ) : (
                service.description
              ) }
          </Text>
        </Block>
      ) }
    </Block>
  );
}

ServiceInfo.propTypes = {
  service: PropTypes.object,
  serviceStyles: PropTypes.object,
  selectedAvailableTime: PropTypes.string,
  modifyLink: PropTypes.func,
  isImageShown: PropTypes.bool,
  isDescriptionShown: PropTypes.bool,
  isDescriptionTrimmable: PropTypes.bool,
  staticContent: PropTypes.object.isRequired,
  depositShown: PropTypes.bool
};

ServiceInfo.defaultProps = {
  service: {},
  serviceStyles: {},
  isDescriptionShown: false,
  isDescriptionTrimmable: false
};

export default ServiceInfo;
